import {
  FLOAT_LENGTH_FEET_TYPE,
  INT_DIAM_INCHES_TYPE,
  isMapFeaturePropPresent,
  MAPBOX_PIPELINE_BUXUS_ID_KEY,
  orderMapFeaturePropsForPopup,
  PHONE_TYPE,
  PIPELINE_MAP_TAP_DENY_TITLE,
  pipelineMapTapDenyMessage,
  type PipelineMapTapDenyReason,
} from '@buxus/shared'
import {
  DEFAULT_OPERATOR_SCREEN,
  pathForOperatorRoute,
  type OperatorContentRoute,
} from '../navigation/operatorRoute'
import { resolvePipelineViewOperatorTarget } from '../mapPipelinePopupDom'
import { CloseIcon } from './pageChrome'
import type { MapFeatureTap } from './ScenarioMapHost'

function formatPropValue(
  prop: { type: number; valueSuffix?: string },
  raw: unknown,
): string {
  if (raw === null || raw === undefined) return ''
  if (prop.type === PHONE_TYPE && typeof raw === 'number') return String(raw)
  if (prop.type === FLOAT_LENGTH_FEET_TYPE && typeof raw === 'number') {
    const v = raw > 0 && raw < 1 ? Math.round(raw * 10) / 10 : Math.trunc(raw)
    return `${v}${prop.valueSuffix ?? ''}`
  }
  if (prop.type === INT_DIAM_INCHES_TYPE && typeof raw === 'number') {
    return `${Math.trunc(raw)}${prop.valueSuffix ?? ''}`
  }
  return `${raw}${prop.valueSuffix ?? ''}`
}

function openOperatorPath(route: OperatorContentRoute, operatorId: string): void {
  const path = pathForOperatorRoute(operatorId, route)
  window.open(path, '_blank', 'noopener,noreferrer')
}

export function MapAssetPanel({
  hits,
  index,
  operators,
  onIndex,
  onClose,
  restrictedReason,
}: {
  hits?: MapFeatureTap[]
  index?: number
  operators?: readonly { operatorId: string; name?: string | null }[]
  onIndex?: (index: number) => void
  onClose: () => void
  /** When set, show jurisdiction deny copy instead of pipeline specs. */
  restrictedReason?: PipelineMapTapDenyReason | null
}) {
  if (restrictedReason) {
    return (
      <div className="sc-asset-overlay" role="dialog" aria-label="Pipeline segment unavailable">
        <div className="sc-account-pane-hd">
          <h2 className="sc-account-pane-title">{PIPELINE_MAP_TAP_DENY_TITLE}</h2>
          <button
            type="button"
            className="sc-btn sc-account-pane-x"
            aria-label="Close map asset"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className="sc-rscroll sc-asset-panel">
          <p className="sc-asset-restricted-message">{pipelineMapTapDenyMessage(restrictedReason)}</p>
        </div>
      </div>
    )
  }

  if (!hits?.length || index == null || !operators || !onIndex) return null
  const hit = hits[index]
  if (!hit) return null
  const title = hits.length > 1 ? `${hits.length} map assets` : 'Map asset'
  const propsObj = hit.properties
  const ordered = orderMapFeaturePropsForPopup()
  const operatorId = resolvePipelineViewOperatorTarget(propsObj, operators)
  const assetRaw = propsObj[MAPBOX_PIPELINE_BUXUS_ID_KEY]
  const assetId =
    typeof assetRaw === 'string'
      ? assetRaw.trim()
      : typeof assetRaw === 'number' && Number.isFinite(assetRaw)
        ? String(Math.trunc(assetRaw))
        : ''

  return (
    <div className="sc-asset-overlay" role="dialog" aria-label={title}>
      <div className="sc-account-pane-hd">
        <h2 className="sc-account-pane-title">{title}</h2>
        <button
          type="button"
          className="sc-btn sc-account-pane-x"
          aria-label="Close map asset"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="sc-rscroll sc-asset-panel">
        <p className="sc-asset-name">{hit.name}</p>
        {hits.length > 1 ? (
          <div className="sc-asset-pager">
            <button
              type="button"
              className="sc-btn"
              disabled={index <= 0}
              onClick={() => onIndex(index - 1)}
            >
              Previous
            </button>
            <span>
              {index + 1} of {hits.length}
            </span>
            <button
              type="button"
              className="sc-btn"
              disabled={index + 1 >= hits.length}
              onClick={() => onIndex(index + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
        <dl className="sc-asset-fields">
          {hit.featureId != null ? (
            <div>
              <dt>ID</dt>
              <dd>{String(hit.featureId)}</dd>
            </div>
          ) : null}
          {ordered.map((prop) => {
            const raw = propsObj[prop.key]
            if (!Object.prototype.hasOwnProperty.call(propsObj, prop.key)) return null
            if (!isMapFeaturePropPresent(raw)) return null
            const display = formatPropValue(prop, raw)
            if (!display.trim()) return null
            return (
              <div key={prop.key}>
                <dt>{prop.label}</dt>
                <dd>
                  {prop.type === PHONE_TYPE ? (
                    <a href={`tel:${encodeURIComponent(`${raw}`.replace(/\s/g, ''))}`}>{display}</a>
                  ) : (
                    display
                  )}
                </dd>
              </div>
            )
          })}
        </dl>
        {operatorId ? (
          <div className="sc-asset-links">
            <button
              type="button"
              className="sc-btn"
              onClick={() =>
                openOperatorPath(
                  { scope: 'operator', screenId: DEFAULT_OPERATOR_SCREEN, assetId: null },
                  operatorId,
                )
              }
            >
              View operator
            </button>
            {assetId ? (
              <button
                type="button"
                className="sc-btn"
                onClick={() =>
                  openOperatorPath(
                    { scope: 'assets', screenId: 'screen.assetOverview', assetId },
                    operatorId,
                  )
                }
              >
                View asset
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
